/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  MemberPasswordChange,
  MemberProfile,
  MemberProfileUpdate,
  MemberRegisterRequest,
  PublicUser,
} from '@secretsoverflow/types';
import { api, ApiClientError, setAccessToken } from './api';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  user: PublicUser | null;
  /**
   * The Secrets profile behind the session, when it belongs to a member. Admins
   * and other locally-created accounts have a `user` but no `member`.
   */
  member: MemberProfile | null;
  status: AuthStatus;
  isAdmin: boolean;
  memberLogin: (email: string, password: string) => Promise<void>;
  memberRegister: (input: MemberRegisterRequest) => Promise<{ pendingReason: string | null }>;
  updateMember: (patch: MemberProfileUpdate) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  changePassword: (input: MemberPasswordChange) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [member, setMember] = useState<MemberProfile | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  // On load, try to restore a session using the httpOnly refresh cookie.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { accessToken } = await api.auth.refresh();
        setAccessToken(accessToken);
        const me = await api.auth.me();
        // Members and admins share the same session and refresh cookie. Only
        // members have a Secrets profile behind them, so a failure here just
        // means this session isn't one.
        const profile = await api.member.me().catch(() => null);
        if (!cancelled) {
          setUser(me);
          setMember(profile);
          setStatus('authenticated');
        }
      } catch (error) {
        if (!(error instanceof ApiClientError)) {
          console.error('Session restore failed', error);
        }
        if (!cancelled) {
          setAccessToken(null);
          setUser(null);
          setStatus('anonymous');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.auth.login({ email, password });
    setAccessToken(res.accessToken);
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const register = useCallback(
    async (input: { email: string; password: string; firstName?: string; lastName?: string }) => {
      const res = await api.auth.register(input);
      setAccessToken(res.accessToken);
      setUser(res.user);
      setStatus('authenticated');
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
      setMember(null);
      setStatus('anonymous');
    }
  }, []);

  const memberLogin = useCallback(async (email: string, password: string) => {
    const res = await api.member.login({ email, password });
    setAccessToken(res.accessToken);
    setMember(res.member);
    setUser(await api.auth.me());
    setStatus('authenticated');
  }, []);

  const memberRegister = useCallback(async (input: MemberRegisterRequest) => {
    const res = await api.member.register(input);
    // No token means the account exists but still needs verifying or approving.
    if (!res.accessToken) return { pendingReason: res.pendingReason };
    setAccessToken(res.accessToken);
    setMember(res.member);
    setUser(await api.auth.me());
    setStatus('authenticated');
    return { pendingReason: null };
  }, []);

  const updateMember = useCallback(async (patch: MemberProfileUpdate) => {
    setMember(await api.member.update(patch));
  }, []);

  const uploadAvatar = useCallback(async (file: File) => {
    setMember(await api.member.uploadAvatar(file));
  }, []);

  const changePassword = useCallback(async (input: MemberPasswordChange) => {
    await api.member.changePassword(input);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      member,
      status,
      isAdmin: user?.role === 'admin',
      login,
      register,
      logout,
      memberLogin,
      memberRegister,
      updateMember,
      uploadAvatar,
      changePassword,
    }),
    [user, member, status, login, register, logout, memberLogin, memberRegister, updateMember, uploadAvatar, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
